import { useState, useRef, useCallback } from 'react'
import type { AudioRecorderState } from '../types'

/**
 * Custom hook for managing audio recording functionality.
 * 
 * @returns Object with recording state and control functions
 */
export const useAudioRecorder = () => {
  const [state, setState] = useState<AudioRecorderState>({
    isRecording: false,
    mediaRecorder: null,
    audioChunks: []
  })
  
  const streamRef = useRef<MediaStream | null>(null)

  /**
   * Starts audio recording with optimal settings.
   * 
   * @returns Promise<void>
   */
  const startRecording = useCallback(async (): Promise<void> => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true
        }
      })

      streamRef.current = stream

      const options = {
        mimeType: 'audio/webm;codecs=opus',
        audioBitsPerSecond: 128000
      }

      const mediaRecorder = new MediaRecorder(stream, options)
      const audioChunks: Blob[] = []

      mediaRecorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          audioChunks.push(event.data)
        }
      }

      mediaRecorder.onstart = () => {
        setState(prev => ({
          ...prev,
          isRecording: true,
          mediaRecorder,
          audioChunks: []
        }))
      }

      mediaRecorder.start(1000) // 1 second chunks
    } catch (error) {
      console.error('Error starting recording:', error)
      throw error
    }
  }, [])

  /**
   * Stops audio recording and returns the recorded audio blob.
   * 
   * @returns Promise<Blob> The recorded audio data
   */
  const stopRecording = useCallback((): Promise<Blob> => {
    return new Promise((resolve, reject) => {
      if (!state.mediaRecorder) {
        reject(new Error('No active recording'))
        return
      }

      state.mediaRecorder.onstop = () => {
        try {
          const audioBlob = new Blob(state.audioChunks, {
            type: 'audio/webm;codecs=opus'
          })

          // Stop all tracks
          if (streamRef.current) {
            streamRef.current.getTracks().forEach(track => track.stop())
            streamRef.current = null
          }

          setState(prev => ({
            ...prev,
            isRecording: false,
            mediaRecorder: null,
            audioChunks: []
          }))

          resolve(audioBlob)
        } catch (error) {
          reject(error)
        }
      }

      state.mediaRecorder.stop()
    })
  }, [state.mediaRecorder, state.audioChunks])

  /**
   * Requests microphone permission from the user.
   * 
   * @returns Promise<boolean> Whether permission was granted
   */
  const requestMicrophonePermission = useCallback(async (): Promise<boolean> => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      stream.getTracks().forEach(track => track.stop()) // Stop immediately after permission check
      return true
    } catch (error) {
      console.error('Microphone permission denied:', error)
      return false
    }
  }, [])

  return {
    isRecording: state.isRecording,
    startRecording,
    stopRecording,
    requestMicrophonePermission
  }
}
